import React, { createContext, useState, useContext, useEffect } from 'react';
import encryptedStorage from '../utils/encryptedStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      if (!encryptedStorage.isAvailable()) {
        console.error('Session storage is not available');
        setLoading(false);
        setIsInitialized(true);
        return;
      }

      try {
        const storedUser = encryptedStorage.getItem('auth_user');
        const storedDomain = encryptedStorage.getItem('current_domain');
        const storedSites = encryptedStorage.getItem('user_sites');

        // console.log('Initializing auth from storage:', {
        //   hasUser: !!storedUser,
        //   hasDomain: !!storedDomain,
        //   hasSites: !!storedSites
        // });

        if (storedUser) {
          setUser(storedUser);
          setCurrentDomain(storedDomain);
          setSites(storedSites || []);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

const login = (userData, domains = []) => {
  try {
    // console.log('Logging in user:', userData.email);
    
    encryptedStorage.setItem('auth_user', userData);
    encryptedStorage.setItem('user_sites', domains);
    let initialDomain = null;
    if (domains && domains.length > 0) {
      initialDomain = domains[0];
      encryptedStorage.setItem('current_domain', initialDomain);
  
      if (initialDomain.role) {
        sessionStorage.setItem('role', initialDomain.role);
      }
    
      if (initialDomain.source) {
        localStorage.setItem('source', initialDomain.source);
      }
    }
    
    if (userData.role) {
      sessionStorage.setItem('role', userData.role);
    }

    if (userData.is_super_admin) {
      sessionStorage.setItem('is_super_admin', 'true');
    } else {
      sessionStorage.setItem('is_super_admin', 'false');
    }
    
    setUser(userData);
    setSites(domains);
    setCurrentDomain(initialDomain);
    
    // console.log('Login successful, user stored in state');
    return true;
  } catch (error) {
    console.error('Login failed:', error);
    return false;
  }
};
  const logout = () => {
    // console.log('Logging out user');
    encryptedStorage.clear();
    sessionStorage.clear();
    localStorage.clear();
    setUser(null);
    setCurrentDomain(null);
    setSites([]);
  };

const switchDomain = (domainId) => {
  if (!sites || sites.length === 0) return false;
  
  const domain = sites.find(d => d.domain_token === domainId);
  if (domain) {
    encryptedStorage.setItem('current_domain', domain);
    setCurrentDomain(domain);
    
    if (domain.role) {
      sessionStorage.setItem('role', domain.role);
    }
    
    if (domain.source) {
      localStorage.setItem('source', domain.source);
    }
    
    return true;
  }
  return false;
};
  const updateSites = (newSites) => {
    encryptedStorage.setItem('user_sites', newSites);
    setSites(newSites);
    
    if (currentDomain && !newSites.find(s => s.domain_token === currentDomain.domain_token)) {
      const newDomain = newSites[0] || null;
      encryptedStorage.setItem('current_domain', newDomain);
      setCurrentDomain(newDomain);
      if (newDomain && newDomain.role) {
        localStorage.setItem('role', newDomain.role);
      }
    }
  };

  const getDomains = () => {
    return sites || [];
  };

  const isAuthenticated = () => {
    const hasUserInState = !!user;
    const hasUserInStorage = !!encryptedStorage.getItem('auth_user');
    
    // console.log('Auth check:', { hasUserInState, hasUserInStorage });
    
    return hasUserInState || hasUserInStorage;
  };

  const getAuthHeaders = () => {
    const currentUser = user || encryptedStorage.getItem('auth_user');
    if (!currentUser) return {};
    
    return {
      'EMAIL': currentUser?.email || ''
    };
  };

  const getCurrentRole = () => {
    return localStorage.getItem('role') || user?.role || null;
  };

  const value = {
    user,
    currentDomain,
    sites,
    loading,
    isInitialized,
    
    login,
    logout,
    switchDomain,
    updateSites,
    getDomains,
    isAuthenticated,
    getAuthHeaders,
    getCurrentRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;